// RG-580 — funciones puras de la pantalla de revisión (sin React), para poder probarlas por separado.

// Leyenda de versión del título: «Versión 3 (Actualización)»; la versión 1 solo lleva «Versión 1» (el prototipo no define sufijo).
export const tituloVersion = (informe) => {
    if (!informe) return '';
    return informe.nroVersion > 1 ? `Versión ${informe.nroVersion} (Actualización)` : `Versión ${informe.nroVersion}`;
}

// Texto previo a la insignia «Modificado» del aviso amarillo; null si no hay versión anterior con la que comparar.
export const avisoActualizacion = (informe, versionAnterior) => {
    if (!informe || !versionAnterior) return null;
    return {
        antes: `Esta es una actualización (Versión ${informe.nroVersion}). Los campos resaltados `,
        despues: ` cambiaron respecto a la Versión ${versionAnterior}.`
    };
}

// Aviso de la sección 7 cuando hay una decisión de un informe anterior (RN-14).
export const avisoDecisionPrevia = (seccion7Previa) => {
    if (!seccion7Previa || !seccion7Previa.fechaRevision) return null;
    return `Se presenta decisión indicada en informe anterior con fecha de revisión ${seccion7Previa.fechaRevision}. Puede actualizarla si corresponde.`;
}

// La marca `conciliar` la entrega el servidor (cde_tin_agrupacion = 1 del IND 311). Sin decisión elegida no hay panel de montos.
export const decisionEsConciliar = (combos, idDecision) => {
    if (idDecision === '' || idDecision === null || idDecision === undefined) return false;
    const lista = Array.isArray(combos && combos.decisionCompania) ? combos.decisionCompania : [];
    const decision = lista.find((d) => String(d.id) === String(idDecision));
    return !!(decision && decision.conciliar === true);
}

// Monto en pesos para mostrar en el campo: «$ 3.500.000». Los montos nacen de esta pantalla (solo enteros): si el dato trae
// centavos se redondea a la vista.
export const formatoPesos = (monto) => {
    const numero = Math.round(Number(monto));
    if (!Number.isFinite(numero)) return '$ 0';
    return `$ ${numero.toLocaleString('es-CO')}`;
}

// Texto inicial del campo de monto por tercero: el de la decisión anterior (RN-14) o «$ 0» si no tenía uno registrado.
export const montosIniciales = (implicados, seccion7Previa) => {
    const lista = Array.isArray(implicados) ? implicados : [];
    const previos = Array.isArray(seccion7Previa && seccion7Previa.montosConciliar) ? seccion7Previa.montosConciliar : [];
    const resultado = {};
    lista.forEach((implicado) => {
        const previo = previos.find((m) => m.idImplicado === implicado.idImplicado);
        resultado[implicado.idImplicado] = formatoPesos(previo ? previo.monto : 0);
    });
    return resultado;
}

// ─── Campo de monto (unidad B) ────────────────────────────────────────────────
// El campo es solo de ENTEROS (pesos, sin centavos) y RECHAZA en vez de coaccionar: un texto que no sea un entero (con o sin
// separador de miles de punto) no se convierte nunca en un número (así «3.500.000,50» no puede llegar como 350000050).

export const MSG_MONTO_INVALIDO = 'Ingrese un monto entero, sin decimales';

// `\d` es ASCII (sin la bandera u): los dígitos de otros alfabetos no cuentan.
const PATRON_MONTO = /^\s*\$?\s*(\d+|\d{1,3}(\.\d{3})+)\s*$/;

// Vacío → 0; texto válido → el entero; cualquier otro texto → null (nunca un número).
export const aEnteroDesdeTexto = (texto) => {
    const valor = texto === null || texto === undefined ? '' : String(texto);
    if (valor.trim() === '') return 0;
    if (!PATRON_MONTO.test(valor)) return null;
    return Number(valor.replace(/[$\s.]/g, ''));
}

export const montoValido = (texto) => aEnteroDesdeTexto(texto) !== null;

// Lo que el usuario escribe o pega se guarda TAL CUAL: no se reescribe mientras escribe (la máscara anterior lo transformaba).
export const alCambiarMonto = (montos, idImplicado, texto) => ({ ...montos, [idImplicado]: texto });

// Al entrar al campo se QUITA el formato («$ 3.500.000» → «3500000»; «$ 0» → vacío) para que el usuario escriba solo dígitos sin
// pelearse con los puntos de miles. Un texto inválido queda intacto para que lo corrija. Al salir, `alSalirDelCampo` lo formatea de nuevo.
export const alEntrarAlCampo = (montos, idImplicado) => {
    const texto = montos[idImplicado];
    const entero = aEnteroDesdeTexto(texto);
    if (entero === null) return montos;
    return { ...montos, [idImplicado]: entero === 0 ? '' : String(entero) };
}

// Al salir del campo se reformatea («$ 3.500.000») solo si el texto es válido; si no, queda intacto para que el usuario lo corrija.
export const alSalirDelCampo = (montos, idImplicado) => {
    const texto = montos[idImplicado];
    const entero = aEnteroDesdeTexto(texto);
    if (entero === null) return montos;
    return { ...montos, [idImplicado]: formatoPesos(entero) };
}

const textoDe = (montos, idImplicado) => (montos && montos[idImplicado] !== undefined ? montos[idImplicado] : '');

// Hay algún campo de monto con texto inválido (solo se evalúa con el panel visible: ver puedeGuardar).
export const hayMontoInvalido = (implicados, montos) => {
    const lista = Array.isArray(implicados) ? implicados : [];
    return lista.some((i) => !montoValido(textoDe(montos, i.idImplicado)));
}

// «Guardar Revisión» se habilita con una decisión elegida y, solo si el panel de montos está visible (decisión de conciliar),
// sin ningún monto inválido. Un texto inválido en un panel que ya no se ve (decisión cambiada) no bloquea el guardado.
export const puedeGuardar = ({ decision, combos, implicados, montos }) => {
    if (decision === '' || decision === null || decision === undefined) return false;
    if (!decisionEsConciliar(combos, decision)) return true;
    return !hayMontoInvalido(implicados, montos);
}

// Cuerpo del POST al lambda de revisión. Sin decisión de conciliar se envía `montosConciliar: []` (el servidor igual lo ignora).
// Se envían todos los terceros con su monto entero (el servidor descarta los de monto 0).
export const armaCuerpoRevision = ({ datos, decision, comentario, montos }) => {
    const implicados = Array.isArray(datos && datos.implicados) ? datos.implicados : [];
    const conciliar = decisionEsConciliar(datos && datos.combos, decision);
    const texto = typeof comentario === 'string' ? comentario : '';
    return {
        idCaso: datos.caso.idCaso,
        idInforme: datos.informe.idInforme,
        decisionCompania: Number(decision),
        comentarioInstruccion: texto.trim() === '' ? null : texto,
        montosConciliar: conciliar
            ? implicados.map((i) => ({ idImplicado: i.idImplicado, monto: aEnteroDesdeTexto(textoDe(montos, i.idImplicado)) }))
            : []
    };
}

// Estado inicial de la sección 7: decisión (texto del select), comentario y montos.
export const seccion7Inicial = (datos) => {
    const previa = datos && datos.seccion7Previa ? datos.seccion7Previa : null;
    const decision = previa && previa.decisionCompania !== null && previa.decisionCompania !== undefined
        ? String(previa.decisionCompania)
        : '';
    return {
        decision,
        comentario: previa && previa.comentarioInstruccion ? previa.comentarioInstruccion : '',
        montos: montosIniciales(datos && datos.implicados, previa)
    };
}
